import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Eye,
  Hash,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  User as UsuarioIcono,
  Users,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Cliente, FormaDePagoSri, Sector } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { Interruptor } from '@/shared/ui/Interruptor';
import { Tabla } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import type { Columna } from '@/shared/ui/Tabla';
import { DialogoDeCliente } from './DialogoDeCliente';

const esRuc = (v: string) => v.trim().length === 13;

export function PaginaClientes() {
  const clienteDeConsultas = useQueryClient();

  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);
  const navegar = useNavigate();
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<Cliente | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clientes = useQuery({
    queryKey: ['clientes', buscar, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Cliente>>('/clientes', {
        query: { texto: buscar || undefined, pagina, tamanio },
      }),
  });

  const sectores = useQuery({
    queryKey: ['sectores'],
    queryFn: () => api.get<Sector[]>('/sectores'),
  });

  const formasDePago = useQuery({
    queryKey: ['formas-pago-sri'],
    queryFn: () => api.get<FormaDePagoSri[]>('/clientes/formas-pago-sri'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['clientes'] });
  }

  const cambiarEstado = useMutation({
    mutationFn: (c: Cliente) =>
      api.put<Cliente>(`/clientes/${c.id}`, {
        identificacion: c.identificacion,
        nombre: c.nombre,
        telefono: c.telefono,
        email: c.email,
        direccion: c.direccion,
        sectorId: c.sectorId,
        latitud: c.latitud,
        longitud: c.longitud,
        zutmX: c.zutmX,
        zutmY: c.zutmY,
        formaPagoSri: c.formaPagoSri,
        exentoInteresMora: c.exentoInteresMora,
        activo: !c.activo,
      }),
    onSuccess: refrescar,
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/clientes/${id}`),
    onSuccess: () => {
      const nombre = aEliminar?.nombre ?? '';
      setAEliminar(null);
      refrescar();
      notificar.exito(`Cliente eliminado: ${nombre}`);
    },

    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo eliminar el cliente';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const porSector = new Map((sectores.data ?? []).map((s) => [s.id, s]));

  const columnas: Columna<Cliente>[] = [
    {
      clave: 'nombre',
      encabezado: 'Contribuyente',
      contenido: (c) => (
        <div className="flex items-center gap-2.5">
          <span
            className={`grid size-8 shrink-0 place-items-center rounded-lg ${
              esRuc(c.identificacion)
                ? 'bg-amber-50 text-amber-600'
                : 'bg-indigo-50 text-indigo-500'
            }`}
            title={esRuc(c.identificacion) ? 'Con RUC' : 'Con cédula'}
          >
            {esRuc(c.identificacion) ? (
              <Building2 className="size-3.5" aria-hidden />
            ) : (
              <UsuarioIcono className="size-3.5" aria-hidden />
            )}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-semibold uppercase text-armazon-800">
              {c.nombre}
            </span>
            <span className="importe block text-[11px] text-armazon-500">{c.identificacion}</span>
          </span>
        </div>
      ),
    },
    {
      clave: 'contacto',
      encabezado: 'Contacto',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs text-armazon-600">{c.email ?? <Vacio />}</span>
          <span className="importe block text-[11px] text-armazon-400">{c.telefono ?? '—'}</span>
        </span>
      ),
    },
    {
      clave: 'cuentas',
      encabezado: 'Cuentas',
      clase: 'w-40',
      contenido: (c) => {
        if (c.numerosDeCuenta.length === 0) {

          return <span className="text-[11px] uppercase text-amber-600">Sin cuentas</span>;
        }
        const [primera, ...resto] = c.numerosDeCuenta;
        return (
          <span className="flex items-center gap-1.5">
            <Hash className="size-3 shrink-0 text-armazon-300" aria-hidden />
            <span className="importe truncate text-xs text-armazon-700">{primera}</span>
            {resto.length > 0 && (
              <span
                className="shrink-0 rounded-full bg-armazon-100 px-1.5 py-0.5 text-[10px] font-semibold text-armazon-600"
                title={c.numerosDeCuenta.join(', ')}
              >
                +{resto.length}
              </span>
            )}
          </span>
        );
      },
    },
    {
      clave: 'sector',
      encabezado: 'Sector',
      clase: 'w-52',
      contenido: (c) => {
        const sector = c.sectorId == null ? undefined : porSector.get(c.sectorId);
        if (!sector) {
          return <Vacio />;
        }
        return (
          <span className="inline-flex items-center gap-1.5 text-xs uppercase text-armazon-600">
            <MapPin
              className={`size-3 shrink-0 ${
                sector.ruralidad === 'rural' ? 'text-marca-500' : 'text-blue-500'
              }`}
              aria-hidden
            />
            <span className="truncate">{sector.parroquia}</span>
          </span>
        );
      },
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      clase: 'w-24',
      contenido: (c) => (
        <div className="flex justify-center">
          <Interruptor
            activo={c.activo}
            alCambiar={() => cambiarEstado.mutate(c)}
            etiqueta={`Activar o desactivar ${c.nombre}`}
            deshabilitado={cambiarEstado.isPending}
          />
        </div>
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      clase: 'w-32 pr-5',
      contenido: (c) => (
        <div className="flex justify-center gap-1">
          <button
            type="button"
            onClick={() => navegar(`/clientes/${c.id}`)}
            title="Ver la ficha"
            aria-label={`Ver la ficha de ${c.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
          >
            <Eye className="size-[15px]" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setEditando(c)}
            title="Editar"
            aria-label={`Editar ${c.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
          >
            <Pencil className="size-[15px]" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setAEliminar(c)}
            title="Eliminar"
            aria-label={`Eliminar ${c.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
          >
            <Trash2 className="size-[15px]" aria-hidden />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Users}
        color="indigo"
        titulo="Clientes"
        subtitulo="Quién paga: contribuyentes del catastro"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo cliente"
            aria-label="Nuevo cliente"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <Tabla
        datos={clientes.data}
        cargando={clientes.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(n) => {
          setTamanio(n);
          setPagina(0);
        }}
        iconoVacio={Users}
        mensajeVacio="NINGÚN CONTRIBUYENTE COINCIDE CON LA BÚSQUEDA"
        entidad="clientes"
        herramientas={
          <CampoDeBusqueda
            valor={buscar}
            alCambiar={(v) => {
              setBuscar(v);
              setPagina(0);
            }}
            marcador="Buscar por nombre, cédula/RUC o correo…"
            className="w-full max-w-sm"
          />
        }
      />

      <DialogoDeCliente
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        cliente={editando}
        sectores={sectores.data ?? []}
        formasDePago={formasDePago.data ?? []}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar cliente"
        descripcion="Solo se puede eliminar a quien no tiene predios a su nombre:"
        detalle={aEliminar ? `${aEliminar.nombre} · ${aEliminar.identificacion}` : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function Vacio() {
  return <span className="text-xs text-armazon-300">—</span>;
}
